import {
  Form,
  Input,
  Button,
  Card,
  Typography,
  App,
  Upload,
  Select,
  Switch,
  Row,
  Col,
  InputNumber,
  Spin,
  Space,
  Tag,
  Modal,
} from "antd";
import API from "../../../../services/api";
import { uploadToCloudinary, uploadMultipleToCloudinary } from "../../../../utils/uploadToCloudinary";
import { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import LocationField from "../../../LocationField";
import {
  SaveOutlined,
  ArrowLeftOutlined,
  ExclamationCircleOutlined,
  CloseOutlined,
} from "@ant-design/icons";
import "./EditAds.css";

const { Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;

const CATEGORY_OPTIONS = [
  "billboard",
  "vehicle",
  "walking",
  "wall",
  "shop_front",
  "bus_stop",
  "pole",
];

const VEHICLE_TYPES = ["cycle", "bike", "car", "auto", "bus", "truck"];
const PLACEMENT_POSITIONS = ["front", "back", "side", "top", "full_wrap"];
const SIZE_UNITS = ["ft", "m"];
const TRAFFIC_LEVELS = ["low", "medium", "high"];
const AUDIENCE_TYPES = ["pedestrian", "vehicle"];
const PRICING_UNITS = ["per_day", "per_week", "per_month"];
const SHIFT_TIMINGS = ["morning", "evening", "full_day"];

function EditAds() {
  const [form] = Form.useForm();
  const { message } = App.useApp();
  const navigate = useNavigate();
  const { id } = useParams();

  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);
  const [pageError, setPageError] = useState(false);
  const [locLoading, setLocLoading] = useState(false);
  const [current, setCurrent] = useState(0);
  const [autoDetecting, setAutoDetecting] = useState(false);
  const [showUnsavedModal, setShowUnsavedModal] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [removedImages, setRemovedImages] = useState([]);
  const [stepChanging, setStepChanging] = useState(false);
  const [removedThumbnail, setRemovedThumbnail] = useState(false);
  const [thumbnailError, setThumbnailError] = useState(false);
  const [imagesError, setImagesError] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);

  // Image state
  const [thumbnailList, setThumbnailList] = useState([]);
  const [fileList, setFileList] = useState([]);

  const abortControllerRef = useRef(null);
  const [isFormDirty, setIsFormDirty] = useState(false);
  const watchCategory = Form.useWatch("category", form);

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Same step visibility logic as Ads.js (AddAds)
  const getStepStyle = (step) => ({
    display: current === step ? "block" : "none",
    width: "100%",
  });

  // Same steps as Ads.js
  const steps = [
    "Description",
    "Asset",
    "Location",
    "Visibility",
    "Pricing",
    "Contact",
    "Images",
  ];

  const stepFields = [
    ["title", "description"],
    ["category", "subType", "width", "height", "unit"],
    // ✅ CHANGED: replaced "city" with "locality" to include locality validation, kept city too
    ["address", "locality", "city", "state", "country", "pincode", "latitude", "longitude"],
    ["trafficLevel", "audienceType", "dailyExposureEstimate"],
    ["pricingUnit", "price", "minDuration", "negotiable"],
    ["contactName", "phone", "email"],
    ["thumbnail", "images"],
  ];

  // ── Image helpers ────────────────────────────────────────────────────────
  const normalizeUpload = (e) => e?.fileList || [];

  const createUploadFile = (file, index = 0) => {
    if (!file) return null;
    if (typeof file === "string") {
      return {
        uid: `existing-${index}-${Date.now()}`,
        name: `image-${index + 1}`,
        status: "done",
        url: file,
      };
    }
    return {
      uid: file._id || file.uid || `existing-${index}-${Date.now()}`,
      name: file.name || `image-${index + 1}`,
      status: "done",
      url: file.url || file.path || file.location || file.secure_url,
    };
  };

  // ── Step navigation — same conditional logic as Ads.js ───────────────────
  const next = async () => {
    // Images check (step 6)
    if (current === 6) {
      if (thumbnailList.length === 0) {
        setThumbnailError(true);
        message.error("Please upload a thumbnail image");
        return;
      }
      if (fileList.length === 0) {
        setImagesError(true);
        message.error("Please upload at least one image");
        return;
      }
      setThumbnailError(false);
      setImagesError(false);
    }

    try {
      const category = form.getFieldValue("category");
      let fieldsToValidate = [...stepFields[current]];

      // Step 1: conditional asset fields (same as Ads.js)
      if (current === 1) {
        if (category === "vehicle") {
          fieldsToValidate = [...fieldsToValidate, "vehicleType"];
        } else if (category === "walking") {
          fieldsToValidate = [...fieldsToValidate, "numberOfPeople", "workingHoursPerDay", "shiftTiming"];
        }
        if (["vehicle", "wall", "shop_front", "bus_stop", "pole"].includes(category)) {
          fieldsToValidate = [...fieldsToValidate, "placementPosition", "maxAdsAllowed"];
        }
        if (["billboard", "walking"].includes(category)) {
          fieldsToValidate = [...fieldsToValidate, "maxAdsAllowed"];
        }
      }

      // Step 2: operating area for vehicle/walking
      if (current === 2) {
        if (category === "vehicle" || category === "walking") {
          fieldsToValidate = [...fieldsToValidate, "operatingArea"];
        }
      }

      await form.validateFields(fieldsToValidate);
      setCurrent(current + 1);
      window.scrollTo(0, 0);
    } catch (err) {
      const firstError =
        err.errorFields?.[0]?.errors?.[0] || "Please fill all required fields";
      message.error({ content: firstError, key: "validation-error", duration: 2.5 });
    }
  };

  const prev = () => {
    setCurrent(current - 1);
    window.scrollTo(0, 0);
  };

  const goToStep = useCallback(
    (stepIndex) => {
      if (stepIndex >= 0 && stepIndex < steps.length) {
        setStepChanging(true);
        setCurrent(stepIndex);
        setTimeout(() => {
          window.scrollTo({ top: 0, behavior: "smooth" });
          setStepChanging(false);
        }, 100);
      }
    },
    [steps.length]
  );

  const handleKeyDown = (e, callback) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      callback();
    }
  };


  // ── Fetch existing ad data ────────────────────────────────────────────────
  const fetchAd = async () => {
    if (abortControllerRef.current) abortControllerRef.current.abort();
    abortControllerRef.current = new AbortController();

    try {
      setPageLoading(true);
      setPageError(false);
      const res = await API.get(`/ads/${id}`, {
        signal: abortControllerRef.current.signal,
      });
      const item = res.data?.data || res.data;

      const thumbnailFileList = item?.thumbnail
        ? [createUploadFile(item.thumbnail, 0)].filter(Boolean)
        : [];

      const imagesFileList = Array.isArray(item?.images)
        ? item.images.map((img, index) => createUploadFile(img, index)).filter(Boolean)
        : [];

      setThumbnailList(thumbnailFileList);
      setFileList(imagesFileList);

      const operatingAreaValue = Array.isArray(item?.location?.operatingArea)
        ? item.location.operatingArea.join(", ")
        : "";

      // audienceType — backend stores as joined string, frontend uses array for multi-select
      let audienceTypeValue = item?.visibility?.audienceType;
      if (typeof audienceTypeValue === "string") {
        audienceTypeValue = audienceTypeValue
          .split(" and ")
          .map((s) => s.trim());
      }

      if (!Array.isArray(audienceTypeValue)) {
        audienceTypeValue = [];
      }

      form.setFieldsValue({
        title: item?.title || "",
        description: item?.description || "",
        category: item?.category || undefined,
        subType: item?.subType || "",
        vehicleType: item?.assetDetails?.vehicleType || undefined,
        placementPosition: item?.assetDetails?.placementPosition || undefined,
        width: item?.assetDetails?.size?.width ?? undefined,
        height: item?.assetDetails?.size?.height ?? undefined,
        unit: item?.assetDetails?.size?.unit || undefined,
        maxAdsAllowed: item?.assetDetails?.maxAdsAllowed ?? undefined,
        numberOfPeople: item?.walkingDetails?.numberOfPeople ?? undefined,
        workingHoursPerDay: item?.walkingDetails?.workingHoursPerDay ?? undefined,
        shiftTiming: item?.walkingDetails?.shiftTiming || undefined,
        address: item?.location?.address || "",
        // ✅ ADDED: populate locality field from backend data
        locality: item?.location?.locality || "",
        city: item?.location?.city || "",
        state: item?.location?.state || "",
        country: item?.location?.country || "",
        pincode: item?.location?.pincode || "",
        latitude: item?.location?.coordinates?.coordinates?.[1] ?? undefined,
        longitude: item?.location?.coordinates?.coordinates?.[0] ?? undefined,
        operatingArea: operatingAreaValue,
        trafficLevel: item?.visibility?.trafficLevel || undefined,
        audienceType: audienceTypeValue || undefined,
        dailyExposureEstimate: item?.visibility?.dailyExposureEstimate ?? undefined,
        pricingUnit: item?.pricing?.unit || undefined,
        price: item?.pricing?.price ?? undefined,
        negotiable: !!item?.pricing?.negotiable,
        minDuration: item?.pricing?.minDuration ?? undefined,
        contactName: item?.contact?.name || "",
        phone: item?.contact?.phone || "",
        email: item?.contact?.email || "",
        thumbnail: thumbnailFileList,
        images: imagesFileList,
      });
      setIsFormDirty(false);
    } catch (err) {
      if (err.name !== "AbortError" && err.name !== "CanceledError") {
        setPageError(true);
        message.error({
          content: err.response?.data?.message || "Failed to load ad details",
        });
      }
    } finally {
      setPageLoading(false);
    }
  };

  useEffect(() => {
    if (id) fetchAd();
    return () => {
      if (abortControllerRef.current) abortControllerRef.current.abort();
    };
  }, [id]);

  useEffect(() => {
    const handleOutsideClick = (e) => {
      const clickedDropdown = e.target.closest(
        ".ea-custom-select"
      );

      if (!clickedDropdown) {
        setOpenDropdown(null);
      }
    };

    document.addEventListener(
      "mousedown",
      handleOutsideClick
    );

    return () => {
      document.removeEventListener(
        "mousedown",
        handleOutsideClick
      );
    };
  }, []);

  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (isFormDirty) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [isFormDirty]);

  const handleBack = () => {
    if (isFormDirty) {
      setShowUnsavedModal(true);
    } else {
      navigate("/my-ads");
    }
  };

  // ── Submit — mirrors Ads.js onFinish + backend updateAd logic ────────────
  const onFinish = async (values) => {
    if (thumbnailList.length === 0) {
      message.error("Please upload a thumbnail image before updating");
      return;
    }
    if (fileList.length === 0) {
      message.error("Please upload at least one gallery image before updating");
      return;
    }
    if (fileList.length > 5) {
      message.error("Maximum 5 images allowed");
      return;
    }

    try {
      setLoading(true);

      const operatingArea = values.operatingArea
        ? values.operatingArea
          .split(",")
          .map((v) => v.trim())
          .filter((v) => v.length >= 2)
        : [];

      const data = {
        title: values.title,
        description: values.description,
        category: values.category,
        subType: values.subType || "",
        assetDetails: {
          ...(values.category === "vehicle" && { vehicleType: values.vehicleType }),
          ...(["vehicle", "wall", "shop_front", "bus_stop", "pole"].includes(values.category) && {
            placementPosition: values.placementPosition,
            maxAdsAllowed: Number(values.maxAdsAllowed),
          }),
          size: {
            width: Number(values.width),
            height: Number(values.height),
            unit: values.unit,
          },
          ...(["billboard", "walking"].includes(values.category) && {
            maxAdsAllowed: Number(values.maxAdsAllowed),
          }),
        },
        ...(values.category === "walking" && {
          walkingDetails: {
            numberOfPeople: Number(values.numberOfPeople),
            workingHoursPerDay: Number(values.workingHoursPerDay),
            shiftTiming: values.shiftTiming,
          },
        }),
        location: {
          address: values.address,
          // ✅ ADDED: locality sent to backend on update
          locality: values.locality.trim(),
          city: values.city,
          state: values.state,
          country: values.country,
          pincode: values.pincode,
          coordinates: {
            type: "Point",
            coordinates: [Number(values.longitude), Number(values.latitude)],
          },
          operatingArea,
        },
        visibility: {
          trafficLevel: values.trafficLevel,
          audienceType: Array.isArray(values.audienceType)
            ? values.audienceType
            : [values.audienceType],
          dailyExposureEstimate: Number(values.dailyExposureEstimate) || 0,
        },
        pricing: {
          unit: values.pricingUnit,
          price: Number(values.price),
          negotiable: !!values.negotiable,
          minDuration: Number(values.minDuration),
        },
        contact: {
          name: values.contactName,
          phone: values.phone,
          email: values.email,
        },
        deleteThumbnail: removedThumbnail,
        deleteImages: removedImages,
        imageOrder: fileList.filter((f) => !f.originFileObj).map((f) => f.url),
      };

      // Upload new thumbnail if changed
      const uploadedThumb = thumbnailList[0]?.originFileObj
        ? await uploadToCloudinary(thumbnailList[0].originFileObj, "general")
        : null;

      // Upload new gallery images
      const newFilesToUpload = fileList.filter((f) => f.originFileObj);
      const uploadedImages = newFilesToUpload.length
        ? await uploadMultipleToCloudinary(
          newFilesToUpload.map((f) => f.originFileObj),
          "general"
        )
        : [];

      await API.put(`/ads/${id}`, {
        ...data,

        // ✅ send only thumbnail URL string
        newThumbnail: uploadedThumb?.url || uploadedThumb,

        // ✅ send only image URL strings
        newImages: uploadedImages.map((img) => img.url || img),
      });
      setIsFormDirty(false);
      message.success({ content: "Ad updated successfully" });
      setRemovedImages([]);
      setRemovedThumbnail(false);
      setThumbnailError(false);
      setImagesError(false);
      setTimeout(() => navigate("/my-ads"), 500);
    } catch (err) {
      message.error({
        content: err.response?.data?.message || "Something went wrong. Please try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  const isMobile = windowWidth <= 768;

  if (!pageLoading && pageError) {
    return (
      <div className="ea-page">
        <div className="ea-container">
          <Card className="ea-card">
            <div className="ea-error-state">
              <ExclamationCircleOutlined className="ea-error-icon" />
              <h2>Failed to load ad details</h2>
              <p>Something went wrong while fetching the data. Please try again.</p>
              <Button type="primary" onClick={fetchAd}>
                Retry
              </Button>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="ea-page">
      <div className="ea-container">
        <Spin
          spinning={pageLoading || stepChanging}
          description={stepChanging ? "Loading step..." : "Loading ad details..."}
        >
          {/* Unsaved changes modal */}
          <Modal
            title={
              <Space>
                <ExclamationCircleOutlined style={{ color: "#faad14" }} />
                <span>Unsaved Changes</span>
              </Space>
            }
            open={showUnsavedModal}
            onOk={() => { setShowUnsavedModal(false); navigate("/my-ads"); }}
            onCancel={() => setShowUnsavedModal(false)}
            okText="Leave"
            cancelText="Stay and Continue Editing"
            okButtonProps={{ danger: true }}
            width={isMobile ? "90%" : 520}
          >
            <p>You have unsaved changes. Are you sure you want to leave?</p>
            <Text type="secondary">Your changes will be lost if you don't save them.</Text>
          </Modal>

          <Card className="ea-card">
            {/* Header */}
            <div className="ea-card-header">
              <div className="ea-card-header-left">
                <Tag className="ea-edit-badge">Editing Mode</Tag>
                <h1 className="ea-title">Edit Advertisement</h1>
                <p className="ea-subtitle">Update your advertisement listing details</p>
              </div>
              <Button
                icon={<CloseOutlined />}
                className="ea-back-btn"
                onClick={handleBack}
                aria-label="Go back"
              >
                {!isMobile && "Back to Listing"}
              </Button>
            </div>

            {/* Stepper — same structure as Ads.js */}
            <div className="ea-stepper">
              {steps.map((step, i) => (
                <div
                  key={i}
                  className={`ea-step ${current === i ? "active" : ""} ${current > i ? "done" : ""}`}
                  onClick={() => goToStep(i)}
                  onKeyDown={(e) => handleKeyDown(e, () => goToStep(i))}
                  role="listitem"
                  tabIndex={0}
                  aria-label={`Step ${i + 1}: ${step}`}
                >
                  <div className="ea-step-line-left" />
                  <div className="ea-step-inner">
                    <div className="ea-circle">
                      {current > i ? (
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                          <polyline points="20 6 9 17 4 12" />
                        </svg>
                      ) : (
                        i + 1
                      )}
                    </div>
                    <span className="ea-step-label">{step}</span>
                  </div>
                  <div className="ea-step-line-right" />
                </div>
              ))}
            </div>

            {/* Progress bar */}
            <div className="ea-progress-bar">
              <div
                className="ea-progress-fill"
                style={{ width: `${(current / (steps.length - 1)) * 100}%` }}
              />
            </div>
            <div className="ea-progress-label">
              Step {current + 1} of {steps.length} — {steps[current]}
            </div>

            <Form
              form={form}
              layout="vertical"
              onFinish={onFinish}
              onFinishFailed={({ errorFields }) => {
                const hasImageError = errorFields.some(
                  (ef) => ef.name[0] === "thumbnail" || ef.name[0] === "images"
                );
                if (hasImageError) {
                  const thumbError = errorFields.find((ef) => ef.name[0] === "thumbnail");
                  const imgError = errorFields.find((ef) => ef.name[0] === "images");
                  if (thumbError) setThumbnailError(true);
                  if (imgError) setImagesError(true);
                }
              }}
              onValuesChange={() => setIsFormDirty(true)}
              scrollToFirstError
              validateTrigger="onBlur"
              className="ea-form"
            >
              {/* Step 0: Description */}
              <div style={getStepStyle(0)}>
                <Form.Item
                  name="title"
                  label="Advertisement Title"
                  rules={[
                    { required: true, message: "Please enter a title for your advertisement" },
                    { min: 5, message: "Title must be at least 5 characters long" },
                    {
                      pattern: /^(?=.*[A-Za-z])[A-Za-z0-9][A-Za-z0-9\s.#&-]*$/,
                      message:
                        "Title must include at least one letter and can contain numbers or symbols like # . & -",
                    },
                  ]}
                >
                  <Input placeholder="e.g. Premium Billboard on MG Road" />
                </Form.Item>

                <Form.Item
                  name="description"
                  label="Description"
                  rules={[
                    { required: true, message: "Please provide a description" },
                    { min: 20, message: "Description should be at least 20 characters" },
                  ]}
                >
                  <TextArea
                    rows={5}
                    placeholder="Describe the ad asset, location value, audience reach, and unique selling points..."
                  />
                </Form.Item>
              </div>

              {/* Step 1: Asset */}
              <div style={getStepStyle(1)}>
                <Row gutter={[16, 0]}>
                  <Col xs={24} sm={12}>
                    <Form.Item
                      name="category"
                      label="Category"
                      rules={[{ required: true, message: "Please select a category" }]}
                    >
                      <div
                        className={`ea-custom-select ${openDropdown === "category"
                          ? "active"
                          : ""
                          }`}
                      >
                        <div
                          className="ea-custom-select-trigger"
                          onClick={() =>
                            setOpenDropdown(
                              openDropdown === "category"
                                ? null
                                : "category"
                            )
                          }
                        >
                          {form.getFieldValue("category")
                            ? form
                              .getFieldValue("category")
                              .split("_")
                              .map(
                                (word) =>
                                  word.charAt(0).toUpperCase() +
                                  word.slice(1)
                              )
                              .join(" ")
                            : "Select category"}

                          <span className="ea-custom-arrow">
                            ⌄
                          </span>
                        </div>

                        {openDropdown === "category" && (
                          <div className="ea-custom-dropdown">
                            {CATEGORY_OPTIONS.map((item) => (
                              <div
                                key={item}
                                className="ea-custom-dropdown-item"
                                onClick={() => {
                                  form.setFieldValue(
                                    "category",
                                    item
                                  );

                                  setOpenDropdown(null);
                                }}
                              >
                                {item
                                  .split("_")
                                  .map(
                                    (word) =>
                                      word.charAt(0).toUpperCase() +
                                      word.slice(1)
                                  )
                                  .join(" ")}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </Form.Item>
                  </Col>

                  <Col xs={24} sm={12}>
                    <Form.Item
                      name="subType"
                      label="Sub Type"
                      rules={[
                        { min: 2, message: "Sub type must be at least 2 characters" },
                        { max: 100, message: "Maximum 100 characters" },
                      ]}
                    >
                      <Input placeholder="Optional subtype (e.g. Digital, Static)" />
                    </Form.Item>
                  </Col>
                </Row>

                {/* Vehicle Type */}
                {watchCategory === "vehicle" && (
                  <Row gutter={[16, 0]}>
                    <Col xs={24} sm={12}>
                      <Form.Item
                        name="vehicleType"
                        label="Vehicle Type"
                        rules={[{ required: true, message: "Please select vehicle type" }]}
                      >
                        <div
                          className={`ea-custom-select ${openDropdown === "vehicleType"
                            ? "active"
                            : ""
                            }`}
                        >
                          <div
                            className="ea-custom-select-trigger"
                            onClick={() =>
                              setOpenDropdown(
                                openDropdown === "vehicleType"
                                  ? null
                                  : "vehicleType"
                              )
                            }
                          >
                            {form.getFieldValue("vehicleType")
                              ? form
                                .getFieldValue("vehicleType")
                                .charAt(0)
                                .toUpperCase() +
                              form
                                .getFieldValue("vehicleType")
                                .slice(1)
                              : "Select vehicle type"}

                            <span className="ea-custom-arrow">
                              ⌄
                            </span>
                          </div>

                          {openDropdown === "vehicleType" && (
                            <div className="ea-custom-dropdown">
                              {VEHICLE_TYPES.map((item) => (
                                <div
                                  key={item}
                                  className="ea-custom-dropdown-item"
                                  onClick={() => {
                                    form.setFieldValue(
                                      "vehicleType",
                                      item
                                    );

                                    setOpenDropdown(null);
                                  }}
                                >
                                  {item.charAt(0).toUpperCase() +
                                    item.slice(1)}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </Form.Item>
                    </Col>

                    <Col xs={24} sm={12}>
                      <Form.Item
                        name="placementPosition"
                        label="Placement Position"
                        rules={[{ required: true, message: "Please select placement position" }]}
                      >
                        <div
                          className={`ea-custom-select ${openDropdown === "placementPosition"
                            ? "active"
                            : ""
                            }`}
                        >
                          <div
                            className="ea-custom-select-trigger"
                            onClick={() =>
                              setOpenDropdown(
                                openDropdown ===
                                  "placementPosition"
                                  ? null
                                  : "placementPosition"
                              )
                            }
                          >
                            {form.getFieldValue(
                              "placementPosition"
                            )
                              ? form
                                .getFieldValue(
                                  "placementPosition"
                                )
                                .split("_")
                                .map(
                                  (word) =>
                                    word.charAt(0).toUpperCase() +
                                    word.slice(1)
                                )
                                .join(" ")
                              : "Select placement"}

                            <span className="ea-custom-arrow">
                              ⌄
                            </span>
                          </div>

                          {openDropdown ===
                            "placementPosition" && (
                              <div className="ea-custom-dropdown">
                                {PLACEMENT_POSITIONS.map(
                                  (item) => (
                                    <div
                                      key={item}
                                      className="ea-custom-dropdown-item"
                                      onClick={() => {
                                        form.setFieldValue(
                                          "placementPosition",
                                          item
                                        );

                                        setOpenDropdown(null);
                                      }}
                                    >
                                      {item
                                        .split("_")
                                        .map(
                                          (word) =>
                                            word.charAt(0).toUpperCase() +
                                            word.slice(1)
                                        )
                                        .join(" ")}
                                    </div>
                                  )
                                )}
                              </div>
                            )}
                        </div>
                      </Form.Item>
                    </Col>
                  </Row>
                )}


                {["vehicle", "wall", "shop_front", "bus_stop", "pole"].includes(watchCategory) && (
                  <Row gutter={[16, 0]}>


                    <Col xs={24} sm={12}>
                      <Form.Item
                        name="maxAdsAllowed"
                        label="Max Ads Allowed"
                        rules={[
                          { required: true, message: "Please enter max ads allowed" },
                          { type: "number", min: 1, message: "Must be at least 1" },
                        ]}
                      >
                        <InputNumber style={{ width: "100%" }} placeholder="e.g. 10" min={1} />
                      </Form.Item>
                    </Col>
                  </Row>
                )}

                {["billboard", "walking"].includes(watchCategory) && (
                  <Row gutter={[16, 0]}>
                    <Col xs={24} sm={12}>
                      <Form.Item
                        name="maxAdsAllowed"
                        label="Max Ads Allowed"
                        rules={[
                          { required: true, message: "Please enter max ads allowed" },
                          { type: "number", min: 1, message: "Must be at least 1" },
                        ]}
                      >
                        <InputNumber style={{ width: "100%" }} placeholder="e.g. 10" min={1} />
                      </Form.Item>
                    </Col>
                  </Row>
                )}

                {/* Size */}
                <Row gutter={[16, 0]}>
                  <Col xs={24} sm={8}>
                    <Form.Item
                      name="width"
                      label="Width"
                      rules={[
                        { required: true, message: "Please enter width" },
                        { type: "number", min: 0.1, message: "Width must be greater than 0" },
                      ]}
                    >
                      <InputNumber style={{ width: "100%" }} placeholder="Width" min={0.1} step={0.1} />
                    </Form.Item>
                  </Col>

                  <Col xs={24} sm={8}>
                    <Form.Item
                      name="height"
                      label="Height"
                      rules={[
                        { required: true, message: "Please enter height" },
                        { type: "number", min: 0.1, message: "Height must be greater than 0" },
                      ]}
                    >
                      <InputNumber style={{ width: "100%" }} placeholder="Height" min={0.1} step={0.1} />
                    </Form.Item>
                  </Col>

                  <Col xs={24} sm={8}>
                    <Form.Item
                      name="unit"
                      label="Size Unit"
                      rules={[{ required: true, message: "Please select size unit" }]}
                    >
                      <div
                        className={`ea-custom-select ${openDropdown === "unit"
                          ? "active"
                          : ""
                          }`}
                      >
                        <div
                          className="ea-custom-select-trigger"
                          onClick={() =>
                            setOpenDropdown(
                              openDropdown === "unit"
                                ? null
                                : "unit"
                            )
                          }
                        >
                          {form.getFieldValue("unit")
                            ? form.getFieldValue("unit") ===
                              "ft"
                              ? "Feet"
                              : "Meters"
                            : "Select unit"}

                          <span className="ea-custom-arrow">
                            ⌄
                          </span>
                        </div>

                        {openDropdown === "unit" && (
                          <div className="ea-custom-dropdown">
                            {SIZE_UNITS.map((item) => (
                              <div
                                key={item}
                                className="ea-custom-dropdown-item"
                                onClick={() => {
                                  form.setFieldValue(
                                    "unit",
                                    item
                                  );

                                  setOpenDropdown(null);
                                }}
                              >
                                {item === "ft"
                                  ? "Feet"
                                  : "Meters"}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </Form.Item>
                  </Col>
                </Row>

                {/* Walking Details */}
                {watchCategory === "walking" && (
                  <>
                    <Row gutter={[16, 0]}>
                      <Col xs={24} sm={12}>
                        <Form.Item
                          name="numberOfPeople"
                          label="Number of People"
                          rules={[
                            { required: true, message: "Please enter number of people" },
                            { type: "number", min: 1, message: "Must be at least 1" },
                          ]}
                        >
                          <InputNumber style={{ width: "100%" }} placeholder="e.g. 5" min={1} />
                        </Form.Item>
                      </Col>

                      <Col xs={24} sm={12}>
                        <Form.Item
                          name="workingHoursPerDay"
                          label="Working Hours Per Day"
                          rules={[
                            { required: true, message: "Please enter working hours" },
                            { type: "number", min: 1, max: 24, message: "Must be between 1 and 24" },
                          ]}
                        >
                          <InputNumber style={{ width: "100%" }} placeholder="e.g. 8" min={1} max={24} />
                        </Form.Item>
                      </Col>
                    </Row>

                    <Row gutter={[16, 0]}>
                      <Col xs={24} sm={12}>
                        <Form.Item
                          name="shiftTiming"
                          label="Shift Timing"
                          rules={[{ required: true, message: "Please select shift timing" }]}
                        >
                          <div
                            className={`ea-custom-select ${openDropdown === "shiftTiming"
                              ? "active"
                              : ""
                              }`}
                          >
                            <div
                              className="ea-custom-select-trigger"
                              onClick={() =>
                                setOpenDropdown(
                                  openDropdown ===
                                    "shiftTiming"
                                    ? null
                                    : "shiftTiming"
                                )
                              }
                            >
                              {form.getFieldValue(
                                "shiftTiming"
                              )
                                ? form
                                  .getFieldValue(
                                    "shiftTiming"
                                  )
                                  .split("_")
                                  .map(
                                    (word) =>
                                      word.charAt(0).toUpperCase() +
                                      word.slice(1)
                                  )
                                  .join(" ")
                                : "Select shift timing"}

                              <span className="ea-custom-arrow">
                                ⌄
                              </span>
                            </div>

                            {openDropdown ===
                              "shiftTiming" && (
                                <div className="ea-custom-dropdown">
                                  {SHIFT_TIMINGS.map((item) => (
                                    <div
                                      key={item}
                                      className="ea-custom-dropdown-item"
                                      onClick={() => {
                                        form.setFieldValue(
                                          "shiftTiming",
                                          item
                                        );

                                        setOpenDropdown(null);
                                      }}
                                    >
                                      {item
                                        .split("_")
                                        .map(
                                          (word) =>
                                            word.charAt(0).toUpperCase() +
                                            word.slice(1)
                                        )
                                        .join(" ")}
                                    </div>
                                  ))}
                                </div>
                              )}
                          </div>
                        </Form.Item>
                      </Col>
                    </Row>
                  </>
                )}
              </div>

              {/* Step 2: Location */}
              <div style={getStepStyle(2)}>
                <Form.Item
                  name="address"
                  label="Address"
                  rules={[
                    { required: true, message: "Please enter the Address" },
                    { min: 5, message: "Address must be at least 5 characters" },
                  ]}
                >
                  <Input placeholder="e.g. #45, MG Road, Near City Center Mall" />
                </Form.Item>

                {/* ✅ ADDED: Locality field using LocationField — same as EditCommercialSpace */}
                <Form.Item
                  name="locality"
                  label="Locality"
                  rules={[
                    { required: true, message: "Please enter locality" },
                    { min: 2, message: "Minimum 2 characters" },
                    {
                      pattern: /^(?=.*[A-Za-z])[A-Za-z0-9\s.,#&()-]+$/,
                      message:
                        "Must contain at least one letter. Allowed: letters, numbers, spaces, . , # & ( ) -",
                    },
                  ]}
                >
                  <LocationField
                    form={form}
                    message={message}
                    setAutoDetecting={setAutoDetecting}
                  />
                </Form.Item>

                {/* ✅ City field kept separately */}
                <Form.Item
                  name="city"
                  label="City"
                  rules={[
                    { required: true, message: "Please enter the city" },
                    {
                      pattern: /^[A-Za-z\s.-]+$/,
                      message: "City name should contain only letters",
                    },
                  ]}
                >
                  <Input placeholder="e.g. Bengaluru" />
                </Form.Item>

                {/* Operating area for vehicle/walking */}
                {(watchCategory === "vehicle" || watchCategory === "walking") && (
                  <Form.Item
                    name="operatingArea"
                    label="Operating Area"
                    extra="Separate each area with a comma — e.g. Indiranagar, Koramangala, Whitefield"
                    rules={[
                      { required: true, message: "Please enter operating area" },
                      {
                        validator: (_, value) => {
                          if (!value) return Promise.reject("Please enter operating area");
                          const areas = value
                            .split(",")
                            .map((a) => a.trim())
                            .filter(Boolean);
                          if (areas.length === 0) return Promise.reject("Please enter valid areas");
                          if (areas.some((a) => a.length < 2))
                            return Promise.reject("Each area must be at least 2 characters");
                          return Promise.resolve();
                        },
                      },
                    ]}
                  >
                    <Input placeholder="e.g. Indiranagar, Koramangala, Whitefield" />
                  </Form.Item>
                )}

                <Row gutter={[16, 0]}>
                  <Col xs={24} sm={12}>
                    <Form.Item
                      name="state"
                      label="State"
                      rules={[
                        { required: true, message: "Please enter the State" },
                        {
                          pattern: /^[A-Za-z\s.-]+$/,
                          message: "State name should contain only letters",
                        },
                      ]}
                    >
                      <Input placeholder="e.g. Karnataka" />
                    </Form.Item>
                  </Col>

                  <Col xs={24} sm={12}>
                    <Form.Item
                      name="country"
                      label="Country"
                      rules={[
                        { required: true, message: "Please enter the country" },
                        {
                          pattern: /^[A-Za-z\s.-]+$/,
                          message: "Country name should contain only letters",
                        },
                      ]}
                    >
                      <Input placeholder="e.g. India" />
                    </Form.Item>
                  </Col>
                </Row>

                <Form.Item
                  name="pincode"
                  label="Pincode"
                  rules={[
                    { required: true, message: "Please enter the pincode" },
                    {
                      pattern: /^[1-9][0-9]{5}$/,
                      message: "Enter a valid 6-digit pincode",
                    },
                  ]}
                >
                  <Input maxLength={6} placeholder="e.g. 560038" style={{ maxWidth: 200 }} />
                </Form.Item>

                <Row gutter={[16, 0]}>
                  <Col xs={24} sm={12}>
                    <Form.Item
                      name="latitude"
                      label="Latitude"
                      rules={[
                        { required: true, message: "Please enter latitude" },
                        {
                          validator: (_, value) => {
                            if (value === undefined || value === null)
                              return Promise.reject("Latitude is required");
                            const str = value.toString();
                            if ((str.match(/\./g) || []).length > 1)
                              return Promise.reject("Only one decimal point allowed");
                            const parts = str.split(".");
                            if (parts[1] && parts[1].length > 6)
                              return Promise.reject("Max 6 digits allowed after decimal");
                            if (typeof value !== "number" || Number.isNaN(value))
                              return Promise.reject("Enter a valid latitude");
                            if (value < -90 || value > 90)
                              return Promise.reject("Latitude must be between -90 and 90");
                            return Promise.resolve();
                          },
                        },
                      ]}
                    >
                      <InputNumber
                        style={{ width: "100%" }}
                        min={-90}
                        max={90}
                        step={0.000001}
                        placeholder="e.g. 12.971891"
                        onBlur={() => {
                          const val = form.getFieldValue("latitude");
                          if (typeof val === "number") {
                            form.setFieldsValue({ latitude: Number(val.toFixed(6)) });
                          }
                        }}
                      />
                    </Form.Item>
                  </Col>

                  <Col xs={24} sm={12}>
                    <Form.Item
                      name="longitude"
                      label="Longitude"
                      rules={[
                        { required: true, message: "Please enter longitude" },
                        {
                          validator: (_, value) => {
                            if (value === undefined || value === null)
                              return Promise.reject("Longitude is required");
                            const str = value.toString();
                            if ((str.match(/\./g) || []).length > 1)
                              return Promise.reject("Only one decimal point allowed");
                            const parts = str.split(".");
                            if (parts[1] && parts[1].length > 6)
                              return Promise.reject("Max 6 digits allowed after decimal");
                            if (typeof value !== "number" || Number.isNaN(value))
                              return Promise.reject("Enter a valid longitude");
                            if (value < -180 || value > 180)
                              return Promise.reject("Longitude must be between -180 and 180");
                            return Promise.resolve();
                          },
                        },
                      ]}
                    >
                      <InputNumber
                        style={{ width: "100%" }}
                        min={-180}
                        max={180}
                        step={0.000001}
                        placeholder="e.g. 77.594563"
                        onBlur={() => {
                          const val = form.getFieldValue("longitude");
                          if (typeof val === "number") {
                            form.setFieldsValue({ longitude: Number(val.toFixed(6)) });
                          }
                        }}
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </div>

              {/* Step 3: Visibility */}
              <div style={getStepStyle(3)}>
                <Row gutter={[16, 0]}>
                  <Col xs={24} sm={12}>
                    <Form.Item
                      name="trafficLevel"
                      label="Traffic Level"
                      rules={[{ required: true, message: "Please select traffic level" }]}
                    >
                      <div
                        className={`ea-custom-select ${openDropdown === "trafficLevel"
                          ? "active"
                          : ""
                          }`}
                      >
                        <div
                          className="ea-custom-select-trigger"
                          onClick={() =>
                            setOpenDropdown(
                              openDropdown ===
                                "trafficLevel"
                                ? null
                                : "trafficLevel"
                            )
                          }
                        >
                          {form.getFieldValue(
                            "trafficLevel"
                          )
                            ? form
                              .getFieldValue(
                                "trafficLevel"
                              )
                              .charAt(0)
                              .toUpperCase() +
                            form
                              .getFieldValue(
                                "trafficLevel"
                              )
                              .slice(1)
                            : "Select traffic level"}

                          <span className="ea-custom-arrow">
                            ⌄
                          </span>
                        </div>

                        {openDropdown ===
                          "trafficLevel" && (
                            <div className="ea-custom-dropdown">
                              {TRAFFIC_LEVELS.map((item) => (
                                <div
                                  key={item}
                                  className="ea-custom-dropdown-item"
                                  onClick={() => {
                                    form.setFieldValue(
                                      "trafficLevel",
                                      item
                                    );

                                    setOpenDropdown(null);
                                  }}
                                >
                                  {item.charAt(0).toUpperCase() +
                                    item.slice(1)}
                                </div>
                              ))}
                            </div>
                          )}
                      </div>
                    </Form.Item>
                  </Col>

                  <Col xs={24} sm={12}>
                    <Form.Item
                      name="audienceType"
                      label="Audience Type"
                      rules={[{ required: true, message: "Please select audience type" }]}
                    >
                      <Select
                        mode="multiple"
                        allowClear
                        showSearch
                        placeholder="Search audience type"
                        className="ea-multi-select"
                        optionFilterProp="label"
                        maxTagCount="responsive"
                        classNames={{ popup: { root: "ea-multi-dropdown" } }}
                        getPopupContainer={(trigger) =>
                          trigger.parentNode
                        }
                        options={AUDIENCE_TYPES.map((item) => ({
                          value: item,
                          label:
                            item.charAt(0).toUpperCase() +
                            item.slice(1),
                        }))}
                      />
                    </Form.Item>
                  </Col>
                </Row>

                <Form.Item
                  name="dailyExposureEstimate"
                  label="Daily Exposure Estimate"
                  rules={[{ type: "number", min: 0, message: "Must be 0 or greater" }]}
                >
                  <InputNumber style={{ width: "100%" }} placeholder="e.g. 5000" min={0} />
                </Form.Item>
              </div>

              {/* Step 4: Pricing */}
              <div style={getStepStyle(4)}>
                <Row gutter={[16, 0]}>
                  <Col xs={24} sm={12}>
                    <Form.Item
                      name="pricingUnit"
                      label="Pricing Unit"
                      rules={[{ required: true, message: "Please select pricing unit" }]}
                    >
                      <div
                        className={`ea-custom-select ${openDropdown === "pricingUnit"
                          ? "active"
                          : ""
                          }`}
                      >
                        <div
                          className="ea-custom-select-trigger"
                          onClick={() =>
                            setOpenDropdown(
                              openDropdown ===
                                "pricingUnit"
                                ? null
                                : "pricingUnit"
                            )
                          }
                        >
                          {form.getFieldValue(
                            "pricingUnit"
                          )
                            ? form
                              .getFieldValue(
                                "pricingUnit"
                              )
                              .split("_")
                              .map(
                                (word) =>
                                  word.charAt(0).toUpperCase() +
                                  word.slice(1)
                              )
                              .join(" ")
                            : "Select pricing unit"}

                          <span className="ea-custom-arrow">
                            ⌄
                          </span>
                        </div>

                        {openDropdown ===
                          "pricingUnit" && (
                            <div className="ea-custom-dropdown">
                              {PRICING_UNITS.map((item) => (
                                <div
                                  key={item}
                                  className="ea-custom-dropdown-item"
                                  onClick={() => {
                                    form.setFieldValue(
                                      "pricingUnit",
                                      item
                                    );

                                    setOpenDropdown(null);
                                  }}
                                >
                                  {item
                                    .split("_")
                                    .map(
                                      (word) =>
                                        word.charAt(0).toUpperCase() +
                                        word.slice(1)
                                    )
                                    .join(" ")}
                                </div>
                              ))}
                            </div>
                          )}
                      </div>
                    </Form.Item>
                  </Col>

                  <Col xs={24} sm={12}>
                    <Form.Item
                      name="price"
                      label="Price (₹)"
                      rules={[
                        { required: true, message: "Please enter the price" },
                        { type: "number", min: 1, message: "Price must be greater than 0" },
                      ]}
                    >
                      <InputNumber style={{ width: "100%" }} placeholder="e.g. 5000" min={1} />
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={[16, 0]}>
                  <Col xs={24} sm={12}>
                    <Form.Item
                      name="minDuration"
                      label="Minimum Duration (days)"
                      rules={[
                        { required: true, message: "Please enter minimum duration" },
                        { type: "number", min: 1, message: "Duration must be at least 1 day" },
                      ]}
                    >
                      <InputNumber style={{ width: "100%" }} placeholder="e.g. 30" min={1} />
                    </Form.Item>
                  </Col>
                </Row>

                <Form.Item label="Negotiable" className="ea-switch-item">
                  <Form.Item name="negotiable" valuePropName="checked" noStyle>
                    <Switch />
                  </Form.Item>
                  <span className="ea-switch-label">Price is open to negotiation</span>
                </Form.Item>
              </div>

              {/* Step 5: Contact */}
              <div style={getStepStyle(5)}>
                <Form.Item
                  name="contactName"
                  label="Contact Person Name"
                  rules={[
                    { required: true, message: "Please enter contact name" },
                    {
                      pattern: /^[A-Za-z\s.-]+$/,
                      message: "Name can only contain letters and basic characters",
                    },
                  ]}
                >
                  <Input placeholder="e.g. Rajesh Kumar" />
                </Form.Item>

                <Row gutter={[16, 0]}>
                  <Col xs={24} sm={12}>
                    <Form.Item
                      name="phone"
                      label="Phone Number"
                      rules={[
                        { required: true, message: "Please enter a phone number" },
                        {
                          pattern: /^[6-9]\d{9}$/,
                          message: "Enter a valid 10-digit Indian phone number",
                        },
                      ]}
                    >
                      <Input maxLength={10} placeholder="e.g. 9876543210" />
                    </Form.Item>
                  </Col>

                  <Col xs={24} sm={12}>
                    <Form.Item
                      name="email"
                      label="Email Address"
                      rules={[
                        { required: true, message: "Please enter your email address" },
                        { type: "email", message: "Enter a valid email address" },
                      ]}
                    >
                      <Input placeholder="e.g. contact@company.com" />
                    </Form.Item>
                  </Col>
                </Row>
              </div>

              {/* Step 6: Images */}
              <div style={getStepStyle(6)}>
                {/* Thumbnail */}
                <Form.Item
                  name="thumbnail"
                  label={
                    <span className="ea-label-with-badge">
                      Thumbnail
                      <span className="ea-badge-primary">Main Image</span>
                    </span>
                  }
                  valuePropName="fileList"
                  getValueFromEvent={normalizeUpload}
                  rules={[{ required: true, message: "Please upload a thumbnail image" }]}
                  validateStatus={thumbnailError ? "error" : ""}
                  help={thumbnailError ? "Please upload a thumbnail image" : ""}
                >
                  <Upload.Dragger
                    maxCount={1}
                    accept="image/png,image/jpeg"
                    className="ea-upload"
                    fileList={thumbnailList}
                    beforeUpload={(file) => {
                      const isValidType = ["image/png", "image/jpeg"].includes(file.type);
                      if (!isValidType) {
                        message.error("Only PNG, JPEG files are allowed");
                        return Upload.LIST_IGNORE;
                      }
                      return false;
                    }}
                    onChange={({ fileList: newFileList }) => {
                      if (newFileList.length > 0) {
                        const file = newFileList[0];
                        setThumbnailList([
                          {
                            uid: "thumb-" + Date.now(),
                            name: file.name,
                            url: file.originFileObj
                              ? URL.createObjectURL(file.originFileObj)
                              : file.url,
                            originFileObj: file.originFileObj,
                            status: "done",
                          },
                        ]);
                        setRemovedThumbnail(false);
                        setThumbnailError(false);
                      } else {
                        setThumbnailList([]);
                        setRemovedThumbnail(true);
                      }
                    }}
                    onRemove={() => {
                      setThumbnailList([]);
                      setRemovedThumbnail(true);
                    }}
                  >
                    <div className="ea-upload-inner">
                      <p className="ea-upload-title">Upload Thumbnail</p>
                      <p className="ea-upload-hint">
                        This is the first image users see · PNG, JPEG
                      </p>
                    </div>
                  </Upload.Dragger>
                </Form.Item>

                {/* Gallery images */}
                <Form.Item
                  name="images"
                  valuePropName="fileList"
                  label={
                    <span className="ea-label-with-badge">
                      Advertisement Images
                      <span className="ea-badge-secondary">Up to 5</span>
                    </span>
                  }
                  getValueFromEvent={normalizeUpload}
                  rules={[
                    { required: true, message: "Please upload at least one image" },
                    {
                      validator: () => {
                        if (fileList.length === 0)
                          return Promise.reject("Please upload at least one image");
                        if (fileList.length > 5)
                          return Promise.reject("Maximum 5 images allowed");
                        return Promise.resolve();
                      },
                    },
                  ]}
                  validateStatus={imagesError ? "error" : ""}
                  help={imagesError ? "Please upload at least one image" : ""}
                >
                  <Upload.Dragger
                    className="ea-upload"
                    maxCount={5}
                    accept="image/png,image/jpeg"
                    multiple
                    fileList={fileList}
                    beforeUpload={(file) => {
                      const isValidType = ["image/png", "image/jpeg"].includes(file.type);
                      if (!isValidType) {
                        message.error("Only PNG, JPEG files are allowed");
                        return Upload.LIST_IGNORE;
                      }
                      return false;
                    }}
                    onChange={({ fileList: newFileList }) => {
                      setFileList(newFileList);
                      if (newFileList.length > 0) setImagesError(false);
                    }}
                    onRemove={(file) => {
                      if (file.url && !file.originFileObj) {
                        setRemovedImages((prev) => [...prev, file.url]);
                      }
                      return true;
                    }}
                  >
                    <div className="ea-upload-inner">
                      <p className="ea-upload-title">Upload Advertisement Images</p>
                      <p className="ea-upload-hint">Add up to 5 images · PNG, JPEG</p>
                    </div>
                  </Upload.Dragger>
                </Form.Item>
              </div>

              {/* Navigation buttons — mirrors Ads.js */}
              <div className="ea-btns">
                {current > 0 && (
                  <Button
                    onClick={prev}
                    className="ea-btn-prev"
                    icon={<ArrowLeftOutlined />}
                    disabled={autoDetecting}
                  >
                    Previous
                  </Button>
                )}
                <div className="ea-btns-right">
                  {current < steps.length - 1 && (
                    <Button
                      type="primary"
                      onClick={next}
                      className="ea-btn-next"
                      disabled={autoDetecting}
                    >
                      Next →
                    </Button>
                  )}
                  {current === steps.length - 1 && (
                    <Button
                      type="primary"
                      onClick={async () => {
                        if (thumbnailList.length === 0) {
                          setThumbnailError(true);
                          message.error("Please upload a thumbnail image");
                          return;
                        }
                        if (fileList.length === 0) {
                          setImagesError(true);
                          message.error("Please upload at least one image");
                          return;
                        }
                        setThumbnailError(false);
                        setImagesError(false);
                        form.submit();
                      }}
                      loading={loading}
                      disabled={loading}
                      icon={<SaveOutlined />}
                      className="ea-btn-submit"
                    >
                      {loading ? "Updating..." : "Update Ad Space"}
                    </Button>
                  )}
                </div>
              </div>
            </Form>
          </Card>
        </Spin>
      </div>
    </div>
  );
}

export default EditAds;